import styled from '@emotion/styled/macro';
import {
  Button,
  SlidingDrawer,
  Typography,
  SvgIcon,
  SvgNames,
  Select,
  TextField,
  tokens,
} from '@sunrun/experience-ui-components';
import { useMemo, useState, useEffect } from 'react';
import { ListViewControls } from 'types/prospect';
import {
  formatDateForInput,
  getTodayAsYYYYMMDD,
  convertToISO,
  validateDateRange,
} from 'helpers/dateHelpers';
import {
  DEFAULT_LV_CONTROLS,
  LEAD_STAGE_OPTIONS,
  OPPORTUNITY_STAGE_OPTIONS,
} from 'constants/ListViewFilterConstants';
import {
  LIST_VIEW_SORT_OPTIONS,
  parseSortValue,
  toSortValue,
} from 'constants/ListViewSortConstants';
import { useListViewSortBreakpoints } from 'hooks/useListViewSortBreakpoints';

const ListViewFilterMenu = ({
  filtersOpen,
  setFiltersOpen,
  listViewControls,
  setListViewControls,
  onApplyListViewControl,
}: {
  filtersOpen: boolean;
  setFiltersOpen: (open: boolean) => void;
  listViewControls: ListViewControls;
  setListViewControls: React.Dispatch<React.SetStateAction<ListViewControls>>;
  onApplyListViewControl: (listViewControls: ListViewControls) => void;
}) => {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [dateError, setDateError] = useState('');
  const [leadStage, setLeadStage] = useState('default');
  const [opportunityStage, setOpportunityStage] = useState('default');
  const today = useMemo(() => getTodayAsYYYYMMDD(), []);
  const { isMobile } = useListViewSortBreakpoints();

  useEffect(() => {
    if (filtersOpen) {
      setFromDate(formatDateForInput(listViewControls.beginCreatedDate));
      setToDate(formatDateForInput(listViewControls.endCreatedDate));

      const stage = listViewControls.stage;
      const isOpportunityStage = OPPORTUNITY_STAGE_OPTIONS.some(
        (option) => option.value === stage,
      );
      const isLeadStage = LEAD_STAGE_OPTIONS.some(
        (option) => option.value === stage,
      );

      switch (true) {
        case stage === 'all-leads':
          setLeadStage('all-leads');
          setOpportunityStage('default');
          break;
        case stage === 'all-opportunities':
          setOpportunityStage('all-opportunities');
          setLeadStage('default');
          break;
        case isOpportunityStage:
          setOpportunityStage(stage);
          setLeadStage('default');
          break;
        case isLeadStage:
          setLeadStage(stage);
          setOpportunityStage('default');
          break;
        default:
          setLeadStage('default');
          setOpportunityStage('default');
          break;
      }
      setDateError('');
    }
  }, [
    filtersOpen,
    listViewControls.beginCreatedDate,
    listViewControls.endCreatedDate,
    listViewControls.stage,
  ]);

  useEffect(() => {
    const error = validateDateRange(fromDate, toDate);
    setDateError(error);
  }, [fromDate, toDate]);

  const handleApply = () => {
    const beginDate = convertToISO(fromDate, false);
    const endDate = convertToISO(toDate, true);

    onApplyListViewControl({
      ...listViewControls,
      beginCreatedDate: beginDate,
      endCreatedDate: endDate,
    });
    setFiltersOpen(false);
  };

  const handleClearAll = () => {
    setDateError('');
    setLeadStage('default');
    setOpportunityStage('default');
    setListViewControls({
      ...DEFAULT_LV_CONTROLS,
      beginCreatedDate: '',
      endCreatedDate: '',
      searchTerm: listViewControls.searchTerm,
    });
    onApplyListViewControl({
      ...DEFAULT_LV_CONTROLS,
      beginCreatedDate: '',
      endCreatedDate: '',
      searchTerm: listViewControls.searchTerm,
    });
    setFiltersOpen(false);
  };

  const handleFilterStageChange = (
    stage: string,
    type: 'lead' | 'opportunity',
  ) => {
    if (type === 'lead') {
      setLeadStage(stage);
      setOpportunityStage('default');
    } else {
      setOpportunityStage(stage);
      setLeadStage('default');
    }
    setListViewControls(
      (current) =>
        ({
          ...current,
          stage: stage,
        } as ListViewControls),
    );
  };

  // This function is necessary due to limitations of the Select component.
  const renderStageSelect = (
    currentValue: string,
    options: typeof LEAD_STAGE_OPTIONS | typeof OPPORTUNITY_STAGE_OPTIONS,
    type: 'lead' | 'opportunity',
  ) => {
    const isDefault = currentValue === 'default';
    return (
      <Select
        key={`${type}-${isDefault ? 'default' : currentValue}`}
        options={options.map(({ label, value }) => ({
          label,
          value,
        }))}
        value={isDefault ? 'default' : currentValue}
        onChange={(e) => handleFilterStageChange(e.target.value, type)}
      />
    );
  };

  const isApplyDisabled = !!dateError;

  return (
    <SlidingDrawer
      isOpen={filtersOpen}
      onClose={() => setFiltersOpen(false)}
      closeOnEscape={true}>
      <FilterContainer>
        <FilterHeader>
          <Typography component="h1" size="large">
            Filters
          </Typography>
          <Button
            color="tertiary"
            size="sm"
            iconMiddle={
              <SvgIcon
                name={SvgNames.Close}
                width={24}
                height={24}
                color={tokens.BLACK}></SvgIcon>
            }
            onClick={() => setFiltersOpen(false)}></Button>
        </FilterHeader>
        <FilterBody>
          <FilterSection>
            {isMobile && (
              <>
                <Typography size="small">Sort</Typography>
                <Select
                  options={LIST_VIEW_SORT_OPTIONS}
                  value={toSortValue(
                    listViewControls.sortParam ?? '',
                    listViewControls.sortDirection ?? 'DESC',
                  )}
                  onChange={(e) => {
                    const { param, direction } = parseSortValue(e.target.value);
                    setListViewControls((prev) => ({
                      ...prev,
                      sortParam: param,
                      sortDirection: direction,
                    }));
                  }}
                />
              </>
            )}
            <Typography size="small">Lead Stage</Typography>
            {renderStageSelect(leadStage, LEAD_STAGE_OPTIONS, 'lead')}
            <Typography size="small">Opportunity Stage</Typography>
            {renderStageSelect(
              opportunityStage,
              OPPORTUNITY_STAGE_OPTIONS,
              'opportunity',
            )}
          </FilterSection>
          <FilterSection>
            <Typography size="small">Creation date</Typography>
            <DateRangeContainer>
              <DateInput
                type="date"
                label=""
                placeholder="From"
                value={fromDate}
                max={today}
                onChange={(e) => setFromDate(e.target.value)}
              />
              {` - `}
              <DateInput
                type="date"
                label=""
                placeholder="To"
                value={toDate}
                max={today}
                onChange={(e) => setToDate(e.target.value)}
              />
            </DateRangeContainer>
            {dateError && <ErrorMessage size="small">{dateError}</ErrorMessage>}
          </FilterSection>
        </FilterBody>
        <FilterFooter>
          <Button color="secondary" onClick={handleClearAll}>
            Clear all
          </Button>
          <Button
            color="primary"
            onClick={handleApply}
            disabled={isApplyDisabled}>
            Apply
          </Button>
        </FilterFooter>
      </FilterContainer>
    </SlidingDrawer>
  );
};

const FilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: scroll;
`;

const FilterHeader = styled.div`
  padding: 1rem;
  flex: 0 0 auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const FilterBody = styled.div`
  padding: 1rem;
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FilterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DateRangeContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const DateInput = styled(TextField)`
  flex: 1;
`;

const ErrorMessage = styled(Typography)`
  color: ${tokens.ERROR_60};
`;

const FilterFooter = styled.div`
  padding: 1rem;
  flex: 0 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 1rem;
`;

export { ListViewFilterMenu, DEFAULT_LV_CONTROLS };
